import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  TextField
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useMemo, useState } from 'react';

import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';
import {
  disconnectHireRight,
  useHireRightConnectionStatus
} from './service';

const NAME = 'HireRight';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  open,
  onClose,
  onSuccessfulIntegration,
  orgId
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
} & IntegrationSetupDialogProperties) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();
  const [refreshKey, setRefreshKey] = useState(0);

  const [status, loading] = useHireRightConnectionStatus(orgId, refreshKey);
  const connected = status?.connected ?? false;

  const [state, setState] = useState({
    domainId: '',
    companyLogin: '',
    apiSubscriptionKey: '',
    userRefId: ''
  });

  if (loading && !status) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Skeleton />
          <Skeleton />
        </Content>
        <DialogActionsWithSlots
          right={
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
          }
        />
      </Dialog>
    );
  }

  if (!connected) {
    const canSave =
      state.domainId.trim() &&
      state.companyLogin.trim() &&
      state.apiSubscriptionKey.trim() &&
      state.userRefId.trim();

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Alert severity="info" icon={false}>
            {ut(
              'Contact your HireRight account representative to obtain your API credentials.'
            )}
          </Alert>

          <FormRow>
            <CustomInputLabel label={ut('Domain ID')} fullWidth>
              <TextField
                variant="outlined"
                value={state.domainId}
                onChange={(ev) =>
                  setState((s) => ({ ...s, domainId: ev.target.value }))
                }
                fullWidth
              />
            </CustomInputLabel>
          </FormRow>

          <FormRow>
            <CustomInputLabel label={ut('Company Login')} fullWidth>
              <TextField
                variant="outlined"
                value={state.companyLogin}
                onChange={(ev) =>
                  setState((s) => ({ ...s, companyLogin: ev.target.value }))
                }
                fullWidth
              />
            </CustomInputLabel>
          </FormRow>

          <FormRow>
            <CustomInputLabel label={ut('API Subscription Key')} fullWidth>
              <TextField
                variant="outlined"
                type="password"
                value={state.apiSubscriptionKey}
                onChange={(ev) =>
                  setState((s) => ({
                    ...s,
                    apiSubscriptionKey: ev.target.value
                  }))
                }
                fullWidth
              />
            </CustomInputLabel>
          </FormRow>

          <FormRow>
            <CustomInputLabel label={ut('User Ref ID (email)')} fullWidth>
              <TextField
                variant="outlined"
                value={state.userRefId}
                onChange={(ev) =>
                  setState((s) => ({ ...s, userRefId: ev.target.value }))
                }
                fullWidth
              />
            </CustomInputLabel>
          </FormRow>
        </Content>
        <DialogActionsWithSlots
          right={
            <>
              <Button color="primary" onClick={onClose}>
                {t('Close')}
              </Button>
              <ButtonWithPromise
                variant="contained"
                color="primary"
                onClick={() =>
                  callFirebaseFunction(
                    'hireright_verifyCredentialsAndStore',
                    {
                      orgId,
                      domainId: state.domainId.trim(),
                      companyLogin: state.companyLogin.trim(),
                      apiSubscriptionKey: state.apiSubscriptionKey.trim(),
                      userRefId: state.userRefId.trim()
                    }
                  ).then(() => {
                    setRefreshKey((k) => k + 1);
                    onSuccessfulIntegration();
                  })
                }
                disabled={!canSave}
                pending={`${ut('Saving')}...`}
              >
                {ut('Save')}
              </ButtonWithPromise>
            </>
          }
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="success">{ut('Connected')}</Alert>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await disconnectHireRight(orgId);
              setRefreshKey((k) => k + 1);
            }}
            pending={`${ut('Disconnecting')}...`}
          >
            {ut('Disconnect')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const HireRightSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const [status] = useHireRightConnectionStatus(org.id, 0);
  const active = useMemo(() => status?.connected ?? false, [status]);

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/hireright.png"
      active={active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
